import { useMemo } from 'react';

import {
  type DEFAULT_AD_FILTER_PARAMS,
  VIOLATIONS_TAB_LIST_AD_FILTER,
} from '../../../shared/constants';
import { violationsTabActions } from '../data-store/violationsTabSlice';

interface UnwrappablePromise<T> extends Promise<T> {
  abort(): void;
  unwrap(): Promise<T>;
}

type QueryMap = Record<
  string,
  (params: typeof DEFAULT_AD_FILTER_PARAMS) => UnwrappablePromise<unknown>
>;

const EMPTY_QUERY_FN: QueryMap = {
  EMPTY_QUERY_FN_KEY: () =>
    ({
      unwrap() {
        return Promise.resolve('empty_query_fn_run_for_query');
      },
    }) as UnwrappablePromise<unknown>,
};

/**
 * Custom hook to generate a query map for advanced filters.
 *
 * This hook retrieves query functions for different advanced filters and
 * returns a memoized object mapping filter keys to their corresponding query functions.
 *
 * @param {boolean} [strict=false] - If `true`, only includes the query function which has initial options;
 *                                   if `false`, includes all types of query functions.
 * @returns {Object} Query map where keys are filter values and values are query functions.
 */
const useGetQueryMap = (strict = false) => {
  const [fetchRiskFilters] = violationsTabActions.useGetRisksMutation();
  const [fetchBusinessProcess] =
    violationsTabActions.useLazyGetBusinessProcessQuery();
  const [fetchAccountFilters] = violationsTabActions.useGetAccountsMutation();
  const [fetchApplicationEndpoints] =
    violationsTabActions.useGetApplicationEndpointsMutation();
  const [fetchUserFilters] = violationsTabActions.useGetUsersMutation();
  const [fetchRulesetFilters] = violationsTabActions.useLazyGetRulesetsQuery();
  const [fetchFuncFilters] = violationsTabActions.useLazyGetFunctionsQuery();
  const [fetchEntitlements] = violationsTabActions.useGetEntitlementsMutation();
  const [fetchEntitlementTypes] =
    violationsTabActions.useLazyGetEntitlementTypesQuery();
  const [fetchMitiatingControls] =
    violationsTabActions.useLazyGetMitiatingControlsQuery();

  const queryMap = useMemo(() => {
    const queryFn = {
      [VIOLATIONS_TAB_LIST_AD_FILTER.APPLICATION.value]:
        fetchApplicationEndpoints,
      [VIOLATIONS_TAB_LIST_AD_FILTER.RULESET.value]: fetchRulesetFilters,
    };
    const isEmptyQueryMap = Boolean(Object.keys(queryFn).length === 0);

    if (strict && isEmptyQueryMap) {
      return EMPTY_QUERY_FN;
    }

    if (strict) {
      return queryFn as QueryMap;
    }

    return {
      ...queryFn,
      [VIOLATIONS_TAB_LIST_AD_FILTER.RISKNAME.value]: fetchRiskFilters,
      [VIOLATIONS_TAB_LIST_AD_FILTER.BUSINESSPROCESS.value]:
        fetchBusinessProcess,
      [VIOLATIONS_TAB_LIST_AD_FILTER.NAME.value]: fetchAccountFilters,
      [VIOLATIONS_TAB_LIST_AD_FILTER.APPLICATION.value]:
        fetchApplicationEndpoints,
      [VIOLATIONS_TAB_LIST_AD_FILTER.ACCOUNTTYPE.value]: fetchAccountFilters,
      [VIOLATIONS_TAB_LIST_AD_FILTER.RULESET.value]: fetchRulesetFilters,
      [VIOLATIONS_TAB_LIST_AD_FILTER.FUNCTION.value]: fetchFuncFilters,
      [VIOLATIONS_TAB_LIST_AD_FILTER.USERNAME.value]: fetchUserFilters,
      [VIOLATIONS_TAB_LIST_AD_FILTER.FIRSTNAME.value]: fetchUserFilters,
      [VIOLATIONS_TAB_LIST_AD_FILTER.LASTNAME.value]: fetchUserFilters,
      [VIOLATIONS_TAB_LIST_AD_FILTER.SYSTEMUSERNAME.value]: fetchUserFilters,
      [VIOLATIONS_TAB_LIST_AD_FILTER.EMPLOYEEID.value]: fetchUserFilters,
      [VIOLATIONS_TAB_LIST_AD_FILTER.COSTCENTER.value]: fetchUserFilters,
      [VIOLATIONS_TAB_LIST_AD_FILTER.JOBCODE.value]: fetchUserFilters,
      [VIOLATIONS_TAB_LIST_AD_FILTER.ENTITLEMENT_VALUE.value]:
        fetchEntitlements,
      [VIOLATIONS_TAB_LIST_AD_FILTER.ENTITLEMENTTYPE.value]:
        fetchEntitlementTypes,
      [VIOLATIONS_TAB_LIST_AD_FILTER.MITIGATINGCTRLNAME.value]:
        fetchMitiatingControls,
    };
  }, [
    strict,
    fetchRiskFilters,
    fetchBusinessProcess,
    fetchAccountFilters,
    fetchApplicationEndpoints,
    fetchUserFilters,
    fetchRulesetFilters,
    fetchFuncFilters,
    fetchEntitlements,
    fetchEntitlementTypes,
    fetchMitiatingControls,
  ]);

  return queryMap;
};

// eslint-disable-next-line import/prefer-default-export
export { useGetQueryMap };
